import type { LearnFAQ } from "@/pages/learn/learnShared";
import { makeEsArticle } from "./learnFactoryEs";

const faqs: LearnFAQ[] = [
  {
    question: "\u00bfA partir de qu\u00e9 edad se puede recibir SAINT?",
    answer:
      "SAINT\u00ae est\u00e1 autorizado por la FDA para adultos con trastorno depresivo mayor que no mejoraron con medicaci\u00f3n antidepresiva previa en el episodio actual. En nuestro sitio describimos el programa para mayores de 22 a\u00f1os. Una cl\u00ednica que ofrece un curso acelerado a un paciente m\u00e1s joven usa el protocolo de forma no indicada \u2014 pida ver el etiquetado.",
  },
  {
    question: "\u00bfPueden los adolescentes recibir TMS?",
    answer:
      "Un dispositivo de TMS est\u00e1ndar (no acelerada) recibi\u00f3 autorizaci\u00f3n de la FDA como tratamiento complementario para adolescentes. La TMS acelerada de 5 d\u00edas en adolescentes es uso no indicado \u2014 no fue el protocolo estudiado en los ensayos de SAINT\u00ae. Un psiquiatra de ni\u00f1os y adolescentes debe guiar cualquier decisi\u00f3n para un menor.",
  },
  {
    question:
      "\u00bfEs seguro que una cl\u00ednica ofrezca TMS de 5 d\u00edas a un ni\u00f1o de 13 a\u00f1os?",
    answer:
      "No hay autorizaci\u00f3n de la FDA para un curso acelerado de TMS de 5 d\u00edas a esa edad, y los ensayos de SAINT\u00ae no estudiaron a adolescentes. No es para alarmarse \u2014 es una pregunta que hacer antes de reservar. Pregunte a la cl\u00ednica: qu\u00e9 evidencia respalda este calendario para la edad de mi hijo, qu\u00e9 dispositivo se usa y qui\u00e9n supervisa.",
  },
  {
    question:
      "\u00bfQu\u00e9 opciones existen para un adolescente con depresi\u00f3n resistente al tratamiento?",
    answer:
      "Las opciones apropiadas para la edad y autorizadas por la FDA, y una evaluaci\u00f3n completa con un psiquiatra de ni\u00f1os y adolescentes, son el punto de partida correcto. Si est\u00e1 en crisis, llame o env\u00ede un mensaje de texto al 988. No ofrecemos SAINT\u00ae a pacientes menores de la edad indicada en el etiquetado de la FDA.",
  },
];

export const esSaintAgeRequirements = makeEsArticle({
  slug: "saint-age-requirements",
  title:
    "Requisitos de edad para SAINT\u00ae TMS: \u00bfpueden los adolescentes recibir SAINT\u00ae o TMS de 5 d\u00edas?",
  description:
    "SAINT\u00ae est\u00e1 autorizado por la FDA para adultos. Esto es lo que significa para adolescentes y adultos j\u00f3venes, y qu\u00e9 saber antes de que una cl\u00ednica ofrezca TMS de 5 d\u00edas a un menor.",
  h1: "\u00bfEst\u00e1 SAINT\u00ae aprobado para adolescentes? Requisitos de edad explicados",
  breadcrumbLabel: "Requisitos de edad de SAINT\u00ae",
  shortAnswer:
    "SAINT\u00ae est\u00e1 autorizado por la FDA para adultos con trastorno depresivo mayor que no mejoraron con medicaci\u00f3n antidepresiva previa. En nuestro sitio describimos el programa para mayores de 22 a\u00f1os. Un dispositivo de TMS est\u00e1ndar (no acelerada) tiene autorizaci\u00f3n de la FDA para adolescentes, pero un curso acelerado de 5 d\u00edas en adolescentes es uso no indicado \u2014 no el protocolo estudiado en los ensayos de SAINT\u00ae.",
  keyTakeaways: [
    "SAINT\u00ae est\u00e1 autorizado por la FDA para adultos; describimos el programa para mayores de 22 a\u00f1os.",
    "Un dispositivo de TMS est\u00e1ndar (no acelerada) tiene autorizaci\u00f3n de la FDA para adolescentes.",
    "Un curso acelerado de 5 d\u00edas en adolescentes es uso no indicado \u2014 no estudiado en los ensayos de SAINT\u00ae.",
    "Si una cl\u00ednica ofrece un programa acelerado a un adolescente, pregunte: \u00bfest\u00e1 autorizado por la FDA para la edad de mi hijo, o es uso no indicado?",
    "Para un adolescente, empiece con un psiquiatra de ni\u00f1os y adolescentes. Si est\u00e1 en crisis, llame o env\u00ede un mensaje de texto al 988.",
  ],
  sections: [
    {
      heading: "\u00bfA partir de qu\u00e9 edad se puede recibir SAINT?",
      paragraphs: [
        "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) est\u00e1 autorizado por la FDA para adultos con trastorno depresivo mayor que no mejoraron con medicaci\u00f3n antidepresiva previa en el episodio actual. En nuestro sitio describimos el programa para mayores de 22 a\u00f1os, para coincidir con nuestra forma de evaluaci\u00f3n y el etiquetado actual que seguimos.",
        "El l\u00edmite de edad existe por qui\u00e9n fue estudiado: los ensayos cl\u00ednicos de SAINT\u00ae inscribieron a adultos. Los resultados de esos ensayos \u2014 79 % de remisi\u00f3n en el ensayo controlado (Cole et al., Am J Psychiatry 2022) \u2014 aplican a esa poblaci\u00f3n adulta. No se pueden asumir para un grupo de edad menor que no fue estudiado.",
      ],
    },
    {
      heading: "\u00bfPueden los adolescentes recibir TMS?",
      paragraphs: [
        "S\u00ed, con una distinci\u00f3n importante. Un dispositivo de TMS est\u00e1ndar (no acelerada) recibi\u00f3 autorizaci\u00f3n de la FDA como tratamiento complementario para adolescentes con depresi\u00f3n. Es un curso convencional de calendario diario, no una semana acelerada de 5 d\u00edas.",
        "Un curso acelerado de TMS de 5 d\u00edas en adolescentes es uso no indicado \u2014 no fue el protocolo probado en los ensayos de SAINT\u00ae, y el etiquetado de SAINT\u00ae que seguimos es para adultos. Un psiquiatra de ni\u00f1os y adolescentes debe guiar cualquier decisi\u00f3n sobre estimulaci\u00f3n cerebral para un menor.",
      ],
    },
    {
      heading:
        "\u00bfEs seguro que una cl\u00ednica ofrezca TMS de 5 d\u00edas a un ni\u00f1o de 13 a\u00f1os?",
      paragraphs: [
        "No hay autorizaci\u00f3n de la FDA para un curso acelerado de TMS de 5 d\u00edas a esa edad, y los ensayos de SAINT\u00ae no estudiaron a adolescentes. No es para alarmarse \u2014 es una pregunta que hacer antes de reservar. Pregunte a la cl\u00ednica: qu\u00e9 evidencia respalda este calendario para la edad de mi hijo, qu\u00e9 dispositivo se usa y qui\u00e9n supervisa la atenci\u00f3n?",
        "No ofrecemos SAINT\u00ae a pacientes menores de la edad indicada en el etiquetado de la FDA. Para un ni\u00f1o o adolescente, el primer paso correcto es una evaluaci\u00f3n completa con un psiquiatra de ni\u00f1os y adolescentes certificado.",
      ],
    },
    {
      heading:
        "\u00bfQu\u00e9 opciones existen para un adolescente con depresi\u00f3n resistente al tratamiento?",
      paragraphs: [
        "Las opciones apropiadas para la edad y autorizadas por la FDA, y una evaluaci\u00f3n completa con un psiquiatra de ni\u00f1os y adolescentes, son el punto de partida correcto. Si su hijo est\u00e1 en crisis, llame o env\u00ede un mensaje de texto al 988 de inmediato \u2014 es la forma m\u00e1s r\u00e1pida de obtener ayuda.",
        "Puede leer m\u00e1s en nuestro contenido para familias: [[\u00bfEs un a\u00f1o dif\u00edcil o una enfermedad real?|/es/teen-depression-hard-year-or-real-illness]] y [[Diagnosticado con depresi\u00f3n como adulto joven|/es/diagnosed-with-depression-as-a-young-adult]]. No ofrecemos SAINT\u00ae acelerada a pacientes menores de la edad indicada en el etiquetado de la FDA.",
      ],
    },
    {
      heading: "Adultos j\u00f3venes (22+): el camino con SAINT\u00ae",
      paragraphs: [
        "Para adultos de 22 a\u00f1os o m\u00e1s con trastorno depresivo mayor que no han mejorado con medicaci\u00f3n antidepresiva, SAINT\u00ae puede ser una opci\u00f3n tras una evaluaci\u00f3n completa. [[Compruebe si puede calificar|/es/learn/who-qualifies-for-saint-tms]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: [
    {
      title: "\u00bfQui\u00e9n califica para SAINT\u00ae TMS?",
      href: "/es/learn/who-qualifies-for-saint-tms",
      description: "Elegibilidad y contraindicaciones para adultos",
    },
    {
      title: "\u00bfQu\u00e9 es SAINT\u00ae TMS?",
      href: "/es/learn/what-is-saint-tms",
      description: "Autorizaci\u00f3n de la FDA, protocolo y evidencia",
    },
    {
      title: "SAINT\u00ae frente a la TMS tradicional",
      href: "/es/learn/saint-vs-traditional-tms",
      description: "C\u00f3mo se compara SAINT\u00ae con la TMS est\u00e1ndar",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  safetyBlock:
    "SAINT\u00ae no es para todos. Est\u00e1 contraindicado para personas con metal sensible a campos magn\u00e9ticos no extra\u00edble cerca de la cabeza, ciertos dispositivos implantados o presi\u00f3n intracraneal aumentada. Los posibles efectos secundarios incluyen cefalea, fatiga, molestias en el cuero cabelludo y ansiedad; la TMS conlleva un peque\u00f1o riesgo de convulsi\u00f3n. Si usted o su hijo est\u00e1n en crisis, llamen o env\u00eden un mensaje de texto al 988.",
  trademarkFooter:
    "SAINT\u00ae es una marca registrada de Stanford University, con licencia exclusiva a Magnus Medical, Inc. Neuro Wellness TMS Centers of America no nombra ni respalda a ninguna otra cl\u00ednica. Los resultados individuales var\u00edan. No se garantiza ning\u00fan resultado. La cobertura var\u00eda seg\u00fan el plan.",
  ctaBand: {
    headline: "\u00bfPreguntas sobre elegibilidad? Preg\u00fantanos, sin costo",
    primaryText: "Vea si puedo ser candidato",
    primaryHref: "/es/learn/who-qualifies-for-saint-tms",
    secondaryText: "Hable con nuestro equipo",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
